import { useEffect, useState } from "react";
import { Form, Col, Row, Button, Modal } from "antd";
import { FaAddressCard } from "react-icons/fa6";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { termOptions } from "../../../utils/Common";
import { hasRole } from "../../../global/hooks/getUserRole";
import { LicenseCodeForm } from "../../LicenseCode/Components/LicenseCodeForm";

export const PurchaseItemForm = ({
  onCancel,
  productionRefetch,
  purchaseRefetch,
  existingItems = [],
}) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const selectedItem = Form.useWatch("Item", form);
  const selectedCodeNo = Form.useWatch("CodeNo", form);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const canCreateCode = hasRole(1, 2);

  const postUrl =
    id && `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const codesUrl =
    selectedItem != null && selectedItem !== ""
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&Item=${selectedItem}`
      : null;

  const { data: optionData, loading: optionDataLoading } = useGet(optionUrl);
  const {
    data: codesData,
    loading: codesLoading,
    refetch: codesRefetch,
  } = useGet(codesUrl);
  const modifiedItems = getModifiedResponse(optionData, "items");
  const licenseCodes = codesData?.data ?? [];
  const usedCodeNos = new Set(
    existingItems
      .map((item) => item?.CodeNo)
      .filter((codeNo) => codeNo != null),
  );
  const codeOptions = licenseCodes
    .filter((item) => !usedCodeNos.has(item.CodeNo))
    .map((item) => ({
      label: item.CodeName || String(item.Code),
      value: item.CodeNo,
    }));

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const fillTermAndPrice = (codeNo) => {
    const selected = licenseCodes.find((item) => item.CodeNo === codeNo);
    form.setFieldsValue({
      Term: selected?.Term,
      Price:
        selected?.Price != null && selected?.Price !== ""
          ? parseFloat(selected.Price).toFixed(2)
          : selected?.Price,
    });
  };

  const handleCodeCreated = (created) => {
    const codeNo = created?.CodeNo;
    if (codeNo == null) {
      codesRefetch?.();
      return;
    }
    const createdItem = created?.Item != null ? Number(created.Item) : null;
    const itemChanged =
      createdItem != null && Number(selectedItem) !== createdItem;
    form.setFieldsValue({
      ...(itemChanged ? { Item: createdItem } : {}),
      CodeNo: codeNo,
    });
    if (!itemChanged) codesRefetch?.();
  };

  const onFinish = async (values) => {
    const payload = {
      CodeNo: Number(values.CodeNo),
      ItemCount:
        values.ItemCount != null ? Number(values.ItemCount) : undefined,
    };
    try {
      await postAction(payload);
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ItemCount: 1,
    });
  }, [form]);

  useEffect(() => {
    if (selectedCodeNo == null || selectedCodeNo === "") return;
    fillTermAndPrice(selectedCodeNo);
  }, [selectedCodeNo, codesData]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      productionRefetch();
      purchaseRefetch?.();
      onCancel();
      openConfirmNotification("Success");
    }
  }, [postError, postData, postLoading]);

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div className="px-2 pt-0 pb-3">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="Item"
                name="Item"
                options={modifiedItems}
                placeholder="Please select"
                required
                loading={optionDataLoading}
                onChange={() => {
                  form.setFieldsValue({
                    CodeNo: undefined,
                    Term: undefined,
                    Price: undefined,
                  });
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="Code"
                name="CodeNo"
                options={codeOptions}
                placeholder="Please select"
                required
                disabled={selectedItem == null || selectedItem === ""}
                loading={codesLoading}
                onChange={fillTermAndPrice}
                handleCreateNew={
                  canCreateCode ? () => setCodeModalOpen(true) : null
                }
                type="Code"
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="Term"
                name="Term"
                options={termOptions}
                placeholder="Please select"
                disabled
                allowClear={false}
                loading={codesLoading}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomInput
                label="Price"
                name="Price"
                type="number"
                disabled
                prefix={"$"}
                style={{ height: "30px" }}
                loading={codesLoading}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomInput
                label="Item Count"
                name="ItemCount"
                type="number"
                integerOnly
                required
              />
            </Col>
          </Row>
        </div>
      </Form>

      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
          onClick={() => form.submit()}
          loading={postLoading}
          disabled={postLoading}
        >
          <CheckCircleOutlined className="!text-[14px] font-medium" />
          <span className="">Apply</span>
        </Button>
        <Button className="reset-button" onClick={onCancel}>
          <CloseOutlined />
          <span className="">Cancel</span>
        </Button>
      </div>
      <Modal
        open={codeModalOpen}
        closable={false}
        onCancel={() => setCodeModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaAddressCard className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Code
            </span>
          </div>
        }
      >
        <LicenseCodeForm
          isModalForm
          defaultValues={{ Item: selectedItem }}
          onCancel={() => setCodeModalOpen(false)}
          onCreated={handleCodeCreated}
        />
      </Modal>
    </>
  );
};
